import React, { useState, useCallback } from 'react';
import {
  Modal,
  Button,
  Card,
  Space,
  Typography,
  Input,
  Spin,
  Image,
  Empty,
  Row,
  Col,
  Popconfirm,
  message,
  Tag,
  Upload,
  Form,
  Select,
} from 'antd';
import {
  PictureOutlined,
  DeleteOutlined,
  CheckOutlined,
  RobotOutlined,
  ReloadOutlined,
  UploadOutlined,
  SaveOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { SourceCharacter, SourceImage, SourceCharacterCheckpoint } from '../../types';
import { sourceStoriesApi, UpdateSourceCharacterCheckpointRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CheckpointImageModalProps {
  visible: boolean;
  character: SourceCharacter;
  checkpoint: SourceCharacterCheckpoint;
  storyId: string;
  onClose: () => void;
}

const CheckpointImageModal: React.FC<CheckpointImageModalProps> = ({
  visible,
  character,
  checkpoint,
  storyId,
  onClose,
}) => {
  const [visualPrompt, setVisualPrompt] = useState(checkpoint.visualPrompt || '');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [infoForm] = Form.useForm();

  const checkpointId = checkpoint.id;

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch checkpoint images
  const {
    data: imagesData,
    loading: imagesLoading,
    refresh: refreshImages,
  } = useApi(
    useCallback(
      () => (visible ? sourceStoriesApi.getCheckpointImages(storyId, checkpointId) : Promise.resolve({ data: [] })),
      [storyId, checkpointId, visible]
    ),
    { immediate: visible }
  );

  const handleGeneratePrompt = async () => {
    await executeAsync(
      async () => {
        const result = await sourceStoriesApi.generateCharacterVisualPrompt(storyId, character.id, additionalInfo, checkpointId);
        setVisualPrompt(result.data.prompt);
      },
      { 
        successMessage: 'Visual prompt generated successfully!',
      }
    );
  };

  const handleGenerateImage = async () => {
    if (!visualPrompt.trim()) {
      message.warning('Please generate or enter a visual prompt first.');
      return;
    }

    setIsGenerating(true);
    try {
      await sourceStoriesApi.generateCheckpointImage(storyId, checkpointId, visualPrompt);
      message.success('Image generated successfully!');
      refreshImages();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickGenerate = async () => {
    setIsGenerating(true);
    const msgKey = 'quick-gen-modal';
    try {
      let currentPrompt = visualPrompt;
      
      // 1. If prompt is empty, generate it first
      if (!currentPrompt.trim()) {
        message.loading({ content: 'Đang gợi ý Visual Prompt...', key: msgKey });
        const result = await sourceStoriesApi.generateCharacterVisualPrompt(storyId, character.id, additionalInfo, checkpointId);
        currentPrompt = result.data.prompt;
        setVisualPrompt(currentPrompt);
      }

      // 2. Generate image
      message.loading({ content: 'Đang tiến hành tạo ảnh AI...', key: msgKey });
      await sourceStoriesApi.generateCheckpointImage(storyId, checkpointId, currentPrompt);
      
      message.success({ content: 'Đã hoàn thành tạo ảnh nhanh!', key: msgKey });
      refreshImages();
    } catch (error: any) {
      message.error({ content: error.message || 'Lỗi khi tạo ảnh nhanh', key: msgKey });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpload = async (file: File) => {
    await executeAsync(
      async () => {
        await sourceStoriesApi.uploadCheckpointImage(storyId, checkpointId, file);
        refreshImages();
        message.success('Image uploaded successfully!');
      },
      { 
        successMessage: 'Image uploaded successfully!' 
      }
    );
  };

  const handleSetAsAvatar = async (imageId: string) => {
    await executeAsync(
      async () => {
        await sourceStoriesApi.setCheckpointAvatar(storyId, checkpointId, imageId);
      },
      { successMessage: 'Avatar updated successfully!' }
    );
    refreshImages();
  };

  const handleDeleteImage = async (imageId: string) => {
    await executeAsync(
      // We still use character-based delete as it's generic for images
      () => sourceStoriesApi.deleteCharacterImage(storyId, character.id, imageId),
      { successMessage: 'Image deleted successfully!' }
    );
    refreshImages();
  };

  const images = imagesData?.data || [];
  const hasImages = images.length > 0;
  const isProcessing = isGenerating || asyncLoading;

  const handleUpdateInfo = async (values: any) => {
    const finalValues: UpdateSourceCharacterCheckpointRequest = {
      ...values,
      visualPrompt: visualPrompt, // Also save visual prompt
    };

    await executeAsync(
      () => sourceStoriesApi.updateSourceCharacterCheckpoint(storyId, character.id, checkpointId, finalValues),
      { successMessage: 'Checkpoint information updated successfully!' }
    );
  };

  const currentAvatarUrl = checkpoint.avatarUrl;

  return (
    <Modal
      title={`Checkpoint: ${checkpoint.name} (Character: ${character.name})`}
      open={visible}
      onCancel={isProcessing ? undefined : onClose}
      footer={null}
      width={1000}
      centered
      maskClosable={false}
      keyboard={false}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '24px'
        }
      }}
    >
      <Row gutter={24}>
        {/* Left Side: Information */}
        <Col span={10}>
          <Card title="Checkpoint Information" size="small">
            <Form
              form={infoForm}
              layout="vertical"
              initialValues={{
                name: checkpoint.name,
                description: checkpoint.description,
              }}
              onFinish={handleUpdateInfo}
            >
              <Form.Item
                name="name"
                label="Checkpoint Name"
                rules={[{ required: true, message: 'Please enter name' }]}
              >
                <Input placeholder="e.g. Adult version" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea rows={4} placeholder="Describe changes in this checkpoint" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={asyncLoading}
                  block
                >
                  Save Information
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Right Side: Visual Identity */}
        <Col span={14}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Step 1: Generate Visual Prompt */}
            <Card 
              title="Step 1: Visual Description" 
              size="small"
              extra={<Tag color="blue">Checkpoint Visuals</Tag>}
            >
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                  Thông tin bổ sung (Để điều chỉnh ngoại hình)
                </Text>
                <TextArea
                  rows={2}
                  placeholder="Ví dụ: mặc áo bào đỏ, tóc bạc trắng, cầm kiếm..."
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  disabled={asyncLoading}
                />
              </div>

              <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                AI Visual Prompt (English)
              </Text>
              <TextArea
                rows={4}
                placeholder="Enter detailed visual description in English for AI image generation..."
                value={visualPrompt}
                onChange={(e) => setVisualPrompt(e.target.value)}
                disabled={asyncLoading}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<RobotOutlined />}
                  onClick={handleGeneratePrompt}
                  loading={asyncLoading}
                >
                  AI Suggest Prompt
                </Button>
              </div>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
                Describe the character's appearance in detail (face, hair, clothing, body type, etc.) in English for optimal AI image generation.
              </Text>
            </Card>

            {/* Step 2: Generate or Upload Image */}
            <Card title="Step 2: Checkpoint Image" size="small" extra={
              <Button
                type="link"
                size="small"
                icon={<ThunderboltOutlined />}
                onClick={handleQuickGenerate}
                loading={isGenerating}
                disabled={asyncLoading}
              >
                Quick Magic (Prompt + Image)
              </Button>
            }>
              <Row gutter={16}>
                <Col span={18}>
                  <Button
                    type="primary"
                    icon={<PictureOutlined />}
                    onClick={handleGenerateImage}
                    loading={isGenerating}
                    disabled={!visualPrompt.trim() || asyncLoading}
                    block
                  >
                    {isGenerating ? 'Generating Image...' : 'Generate Image with AI'}
                  </Button>
                </Col>
                <Col span={6}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleUpload(file);
                      return false;
                    }}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      block
                      disabled={isGenerating || asyncLoading}
                    >
                      Upload
                    </Button>
                  </Upload>
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: '11px', marginTop: 8, display: 'block' }}>
                * New images will be generated using identity consistency from existing checkpoints.
              </Text>
            </Card>

            {/* Step 3: Image Gallery */}
            <Card 
              title={`Image Gallery (${images.length})`} 
              size="small"
              extra={
                hasImages && (
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={refreshImages}
                    loading={imagesLoading}
                  >
                    Refresh
                  </Button>
                )
              }
            >
              {imagesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin />
                </div>
              ) : !hasImages ? (
                <Empty
                  description="No images yet. Generate your first checkpoint image above."
                  style={{ padding: '40px 0' }}
                />
              ) : (
                <Row gutter={[16, 16]}>
                  {images.map((image) => (
                    <Col span={6} key={image.id}>
                      <div style={{ position: 'relative', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ width: '100%', aspectRatio: '9 / 16', overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                          <Image
                            src={image.url}
                            alt="Checkpoint"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
                            preview={{ mask: 'Preview' }}
                          />
                        </div>
                        
                        {currentAvatarUrl === image.url && (
                          <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                            <Tag color="green" style={{ margin: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Avatar</Tag>
                          </div>
                        )}

                        <div style={{ 
                          position: 'absolute', 
                          bottom: 0, 
                          left: 0, 
                          right: 0, 
                          padding: '8px', 
                          background: 'rgba(255,255,255,0.9)', 
                          backdropFilter: 'blur(4px)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px solid #f0f0f0'
                        }}>
                          <Space size={4}>
                            <Button
                              type={currentAvatarUrl === image.url ? 'primary' : 'text'}
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={() => handleSetAsAvatar(image.id)}
                              disabled={currentAvatarUrl === image.url}
                              title="Set as Avatar"
                            />
                            <Popconfirm
                              title="Delete this image?"
                              onConfirm={() => handleDeleteImage(image.id)}
                            >
                              <Button
                                type="text"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                title="Delete"
                              />
                            </Popconfirm>
                          </Space>
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            {formatDate(image.createdAt)}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </Space>
        </Col>
      </Row>
    </Modal>
  );
};

export default CheckpointImageModal;
